import { UserIcon } from "@phosphor-icons/react";
import { shuffle } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../../../../utils";
import { Badge } from "../../../Badge";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "../../../Carousel";
import TextDescription from "../../../TextDescription";

export type PromotedExhibitor = {
  id: string;
  name: string;
  imgUrl: string;
  description: string;
};

const AUTO_SCROLL_INTERVAL = 5000;
const RESET_DELAY = 10000;

export default function PromotedExhibitorsPanel({
  exhibitors,
  onExponentClick,
}: {
  exhibitors?: PromotedExhibitor[];
  onExponentClick: (exhibitorId: string) => void;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const autoScrollTimerRef = useRef<NodeJS.Timeout>(null);
  const resetTimeoutRef = useRef<NodeJS.Timeout>(null);

  const randomizedSort = useMemo(() => {
    if (!exhibitors) return [];

    return shuffle([...exhibitors]);
  }, [exhibitors]);

  const scrollToNext = useCallback(() => {
    if (!api) return;

    if (api.canScrollNext()) {
      api.scrollNext();
    } else {
      api.scrollTo(0);
    }
  }, [api]);

  const startNormalAutoScroll = useCallback(() => {
    if (autoScrollTimerRef.current) {
      clearInterval(autoScrollTimerRef.current);
    }

    if (!api) return;

    autoScrollTimerRef.current = setInterval(
      scrollToNext,
      AUTO_SCROLL_INTERVAL,
    );
  }, [api, scrollToNext]);

  const resetAutoScrollTimer = useCallback(() => {
    if (autoScrollTimerRef.current) {
      clearInterval(autoScrollTimerRef.current);
    }
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
    }

    if (!api) return;

    resetTimeoutRef.current = setTimeout(() => {
      scrollToNext();
      startNormalAutoScroll();
    }, RESET_DELAY);
  }, [api, scrollToNext, startNormalAutoScroll]);

  const handleDotClick = useCallback(
    (index: number) => {
      api?.scrollTo(index);
      resetAutoScrollTimer();
    },
    [api, resetAutoScrollTimer],
  );

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!api) return;

    startNormalAutoScroll();

    const onPointerDown = () => {
      resetAutoScrollTimer();
    };

    api.on("pointerDown", onPointerDown);

    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current);
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      api.off("pointerDown", onPointerDown);
    };
  }, [api, startNormalAutoScroll, resetAutoScrollTimer]);

  if (!exhibitors || exhibitors.length === 0) return null;

  return (
    <div className="flex flex-col items-center gap-3">
      <Carousel
        setApi={setApi}
        className="w-full"
        opts={{
          align: "start",
        }}
      >
        <CarouselContent>
          {randomizedSort.map((exhibitor) => (
            <CarouselItem
              key={exhibitor.id}
              className="basis-full pl-4 md:basis-auto"
            >
              <PromotedExhibitorCard
                exhibitor={exhibitor}
                onExponentClick={onExponentClick}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="flex gap-2">
        {randomizedSort.map((exhibitor, index) => (
          <button
            key={exhibitor.id}
            onClick={() => handleDotClick(index)}
            className={cn(
              "h-2 w-2 rounded-full transition-all",
              current === index
                ? "bg-primary-500 w-4"
                : "bg-gray-300 hover:bg-gray-400",
            )}
            aria-label={`Aller à l'exposant ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function PromotedExhibitorCard({
  exhibitor,
  onExponentClick,
}: {
  exhibitor: PromotedExhibitor;
  onExponentClick: (exhibitorId: string) => void;
}) {
  return (
    <div className="select-none">
      <Badge
        variant="red-orange"
        className="bg-secondary-600 ml-4 rounded-b-none rounded-t-lg border-0 text-xs font-semibold text-white"
      >
        À ne pas manquer
      </Badge>
      <div
        className="flex h-24 gap-2 rounded-lg border border-gray-200 bg-white p-2 md:w-80"
        role="button"
        onClick={() => onExponentClick(exhibitor.id)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onExponentClick(exhibitor.id);
          }
        }}
      >
        <div className="aspect-square size-20 shrink-0 overflow-hidden rounded-lg border border-gray-200">
          {exhibitor.imgUrl ? (
            <img
              className="size-full object-cover"
              src={exhibitor.imgUrl}
              alt={exhibitor.name}
            />
          ) : (
            <div className="flex items-center justify-center size-full">
              <UserIcon size={32} className="text-blue-700" />
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-around overflow-hidden">
          <h3 className="truncate text-sm font-extrabold">{exhibitor.name}</h3>
          <TextDescription className="line-clamp-2 text-xs font-normal text-gray-600">
            {exhibitor.description}
          </TextDescription>
        </div>
      </div>
    </div>
  );
}
